import { useRef, useState, useEffect } from "react";
import { fileUrl } from "@/lib/api";
import { BadgeIcon } from "@/components/Badges";
import UsernameEffect from "@/components/UsernameEffect";
import DiscordPresence from "@/components/DiscordPresence";
import { fontFamily } from "@/components/profileConfig";
import { Eye, Volume2, VolumeX, Play, Pause, Link2, Github, Twitter, Instagram, Youtube, Twitch, Globe, Music2 } from "lucide-react";

const ICONS = {
  link: Link2, github: Github, twitter: Twitter, instagram: Instagram,
  youtube: Youtube, twitch: Twitch, globe: Globe, music: Music2,
};

const shapeClass = {
  circle: "rounded-full",
  square: "rounded-none",
  squircle: "rounded-3xl",
  rounded: "rounded-2xl",
};

export default function ProfileView({ profile, username, views, preview = false }) {
  const p = profile || {};
  const accent = p.accent_color || "#8B5CF6";
  const customFont = fontFamily(p.font);
  const avEffect = p.avatar_effect || "none";
  const shapeCls = shapeClass[p.avatar_shape] || "rounded-full";
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const audioSrc = p.audio_url || (p.background_audio_path ? fileUrl(p.background_audio_path) : "");

  useEffect(() => {
    setPlaying(false);
  }, [audioSrc]);

  const toggleAudio = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play().then(() => setPlaying(true)).catch(() => {}); }
  };

  const bgStyle = p.background_path
    ? { backgroundImage: `url(${fileUrl(p.background_path)})`, backgroundSize: "cover", backgroundPosition: "center" }
    : { backgroundColor: p.background_color || "#08080A" };

  const avatarInner = p.avatar_path ? (
    <img
      src={fileUrl(p.avatar_path)}
      alt={username}
      className={`w-24 h-24 object-cover border-2 ${shapeCls}`}
      style={{ borderColor: accent, boxShadow: `0 0 28px ${accent}66` }}
    />
  ) : (
    <div
      className={`w-24 h-24 flex items-center justify-center text-3xl font-serif font-bold border-2 ${shapeCls}`}
      style={{ background: `linear-gradient(135deg, ${accent}, #f43f5e)`, borderColor: accent, boxShadow: `0 0 28px ${accent}66` }}
    >
      {(username || "?")[0].toUpperCase()}
    </div>
  );

  const layout = p.layout || "classic";
  const isLeft = layout === "left" || layout === "hero";
  const justify = isLeft ? "justify-start" : "justify-center";
  const textAlign = isLeft ? "text-left" : "text-center";
  const cardFontStyle = customFont ? { fontFamily: customFont } : undefined;

  const customBadges = (p.custom_badges || []).filter((b) => b.label || b.image_path);
  const hasBadges = (p.badges && p.badges.length > 0) || customBadges.length > 0;

  const avatarFx = (
    <div className="av-wrap" style={{ "--av-accent": accent }} data-testid="avatar-fx">
      {(avEffect === "rainbow-ring" || avEffect === "spin") && (
        <span className={`av-ring av-ring-${avEffect} ${shapeCls}`} />
      )}
      {avEffect === "particles" &&
        Array.from({ length: 12 }).map((_, i) => {
          const angle = (i / 12) * Math.PI * 2;
          return (
            <span
              key={i}
              className="av-dot"
              style={{
                "--tx": `${Math.cos(angle) * 62}px`,
                "--ty": `${Math.sin(angle) * 62}px`,
                background: i % 2 ? accent : "#ffffff",
                animationDelay: `${i * 0.12}s`,
              }}
            />
          );
        })}
      <div className={`av-inner ${shapeCls} ${avEffect === "glow" ? "av-glow" : ""} ${avEffect === "pulse" ? "av-pulse" : ""}`}>
        {avatarInner}
      </div>
    </div>
  );

  const nameRow = (
    <div className={textAlign}>
      <div className={`flex items-center gap-2 flex-wrap ${justify}`}>
        <h1 className={`text-2xl font-bold ${customFont ? "" : "font-serif"}`} style={cardFontStyle}>
          <UsernameEffect text={p.display_name || username} effect={p.name_effect || "none"} />
        </h1>
      </div>
      <p className="text-sm font-mono text-zinc-400 mt-0.5">@{username}</p>
    </div>
  );

  const badgesRow = hasBadges && (
    <div className={`flex items-center gap-2 flex-wrap ${justify}`} data-testid="profile-badges">
      {(p.badges || []).map((b) => <BadgeIcon key={b} name={b} />)}
      {customBadges.map((b) => (
        <span
          key={b.id}
          data-testid={`custom-badge-${b.id}`}
          title={b.label}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border border-white/15 bg-white/5 text-white"
        >
          {b.image_path && <img src={fileUrl(b.image_path)} alt={b.label} className="w-4 h-4 rounded object-cover" />}
          {b.label}
        </span>
      ))}
    </div>
  );

  const bioRow = p.bio && (
    <p className={`text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap ${textAlign}`}>{p.bio}</p>
  );

  const discordRow = p.discord_enabled && p.discord_id && <DiscordPresence discordId={p.discord_id} />;

  const linksRow = (
    <div className="space-y-3 pt-1 w-full">
      {(p.links || []).filter((l) => l.title && l.url).map((l) => {
        const Icon = ICONS[l.icon] || Link2;
        return (
          <a
            key={l.id}
            href={l.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`profile-link-${l.id}`}
            className="group flex items-center gap-3 w-full rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 px-4 py-3 transition-all hover:-translate-y-0.5"
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = accent)}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)")}
          >
            <Icon className="w-4 h-4" style={{ color: accent }} />
            <span className="text-sm font-medium flex-1 text-left">{l.title}</span>
          </a>
        );
      })}
    </div>
  );

  const statsRow = (
    <div className={`flex items-center gap-4 pt-2 text-xs text-zinc-400 ${justify}`}>
      <span className="inline-flex items-center gap-1.5" data-testid="profile-views">
        <Eye className="w-3.5 h-3.5" /> {views ?? 0} views
      </span>
      {audioSrc && (
        <button onClick={toggleAudio} data-testid="audio-toggle" className="inline-flex items-center gap-1.5 hover:text-white transition-colors">
          {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
          {playing ? "Pause" : "Play"} music
          {playing ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
        </button>
      )}
    </div>
  );

  const bannerTop = p.banner_path && (
    <div className="-mx-6 -mt-6 sm:-mx-8 sm:-mt-8 mb-2 h-28 rounded-t-2xl overflow-hidden">
      <img src={fileUrl(p.banner_path)} alt="banner" className="w-full h-full object-cover" />
    </div>
  );

  let card;
  if (layout === "minimal") {
    card = (
      <div className="relative z-10 w-full max-w-sm space-y-5 text-center flex flex-col items-center" style={cardFontStyle} data-testid="layout-minimal">
        {avatarFx}
        {nameRow}
        {badgesRow}
        {bioRow}
        {discordRow}
        {linksRow}
        {statsRow}
      </div>
    );
  } else if (layout === "hero") {
    card = (
      <div className="relative z-10 w-full max-w-md rounded-2xl border border-white/10 bg-black/40 backdrop-blur-2xl overflow-hidden shadow-2xl" style={cardFontStyle} data-testid="layout-hero">
        <div className="h-32 w-full" style={p.banner_path ? undefined : { background: `linear-gradient(120deg, ${accent}, #0b0b0f)` }}>
          {p.banner_path && <img src={fileUrl(p.banner_path)} alt="banner" className="w-full h-full object-cover" />}
        </div>
        <div className="px-6 pb-6 -mt-12 space-y-4 text-left relative">
          <div className="flex justify-start">{avatarFx}</div>
          {nameRow}
          {badgesRow}
          {bioRow}
          {discordRow}
          {linksRow}
          {statsRow}
        </div>
      </div>
    );
  } else {
    // classic or left
    card = (
      <div
        className={`relative z-10 w-full max-w-md rounded-2xl border border-white/10 bg-black/40 backdrop-blur-2xl p-6 sm:p-8 shadow-2xl space-y-5 ${textAlign}`}
        style={cardFontStyle}
        data-testid={`layout-${layout}`}
      >
        {bannerTop}
        <div className={`flex ${justify}`}>{avatarFx}</div>
        {nameRow}
        {badgesRow}
        {bioRow}
        {discordRow}
        {linksRow}
        {statsRow}
      </div>
    );
  }

  return (
    <div
      data-testid="profile-view"
      className={`relative w-full ${preview ? "h-full rounded-2xl overflow-hidden" : "min-h-screen"} flex flex-col items-center justify-center p-4 overflow-hidden text-white`}
      style={bgStyle}
    >
      <div className="absolute inset-0 bg-black/55 backdrop-blur-[2px]" />
      {audioSrc && <audio ref={audioRef} src={audioSrc} loop onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} />}
      {card}
      {!preview && (
        <a href="/" className="relative z-10 mt-6 text-xs text-zinc-500 hover:text-zinc-300 font-mono transition-colors">
          powered by grime.bio
        </a>
      )}
    </div>
  );
}
